import { Tabs, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import * as TabsPrimitive from "@radix-ui/react-tabs";

/**
 * Tabs that split a page into subjects (a contact's summary, calls,
 * appointments…), drawn as the underlined row the platform back-office's
 * client file uses — not `ui/tabs`' segmented control, which reads as a
 * toggle inside a form rather than as the page's own sections.
 *
 * The row scrolls sideways on a phone instead of wrapping, so the tabs stay
 * one line under the title.
 */
export function PageTabs({
  value,
  onValueChange,
  tabs,
  children,
  className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  tabs: { value: string; label: React.ReactNode }[];
  /** One `PageTabsContent` per tab. */
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Tabs
      value={value}
      onValueChange={onValueChange}
      className={cn("space-y-6", className)}
    >
      <TabsPrimitive.List className="flex overflow-x-auto border-b [scrollbar-width:none]">
        {tabs.map((tab) => (
          <TabsPrimitive.Trigger
            key={tab.value}
            value={tab.value}
            className="-mb-px shrink-0 cursor-pointer border-b-2 border-transparent px-4 py-2 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40 data-[state=active]:border-primary data-[state=active]:text-foreground"
          >
            {tab.label}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {children}
    </Tabs>
  );
}

export function PageTabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsContent>) {
  return <TabsContent className={cn("mt-0 space-y-6", className)} {...props} />;
}
